import { Heading, Select, Stack, TextField } from '@eleveo/components-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { parseDate } from '@internationalized/date';
import { FormApi } from 'final-form';
import { TFunction } from 'i18next';
import { difference } from 'ramda';
import React, { useState } from 'react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { Field, useField } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';

import { EDIT_AGENT_INPUT_FIELD } from './constants';
import { AgentValues } from './types';
import { useAuth } from '../../app/AuthorizationProvider';
import { ModalConfirmationNewStyle } from '../../components/ModalConfirmationNewStyle';
import { Queue, ShiftTemplate, Skill } from '../../graphql/components';
import { recordBy, recordFromIds } from '../../utils/array';
import { shouldShowSearchField } from '../../utils/helpers';
import { TValidator } from '../../utils/validations/types';

type EditAgentFormBodyProps = {
  form: FormApi<AgentValues>;
  isView?: boolean;
  queues: Queue[];
  shiftTemplates: ShiftTemplate[];
  skills: Skill[];
  t: TFunction;
};

type UnsupportedQueueModal = {
  open: boolean;
  queueIds: string[];
  skillIds: string[];
  supportedQueuesOptionIds: string[];
};

/**
 * Edit agent form body.
 */
export const EditAgentFormBody: React.FC<EditAgentFormBodyProps> = ({
  form,
  isView = false,
  queues: allQueues,
  shiftTemplates: allShiftTemplates,
  skills: allSkills,
  t,
}) => {
  const { timezone } = useAuth();

  const formatter = useDateFormatter({ dateStyle: 'medium', timeZone: timezone });

  const [queuesOptions, setQueuesOptions] = useState<Queue[]>(allQueues);
  const [unsupportedQueueModal, useUnsupportedQueueModal] = useState<UnsupportedQueueModal>({
    open: false,
    queueIds: [],
    skillIds: [],
    supportedQueuesOptionIds: [],
  });

  const shiftTemplatesObj = recordBy(allShiftTemplates, 'id');

  const handleUnsupportedQueue = (
    supportedQueues: Queue[],
    previousSkillIds: string[],
    selectedQueueIds: string[] = []
  ) => {
    const supportedQueuesIds = supportedQueues.map(({ id }) => id);

    const unsupportedSelectedQueueIds = difference(selectedQueueIds, supportedQueuesIds);

    if (unsupportedSelectedQueueIds.length) {
      const supportedSelectedQueueIds = difference(selectedQueueIds, unsupportedSelectedQueueIds);

      // eslint-disable-next-line react-hooks/rules-of-hooks
      useUnsupportedQueueModal({
        open: true,
        queueIds: supportedSelectedQueueIds,
        skillIds: previousSkillIds,
        supportedQueuesOptionIds: supportedQueuesIds,
      });
    } else {
      // update queues options to match new skills selection
      setQueuesOptions(supportedQueues);
    }
  };

  const closeAndResetModal = () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useUnsupportedQueueModal({
      open: false,
      queueIds: [],
      skillIds: [],
      supportedQueuesOptionIds: [],
    });
  };

  const handleModalSubmit = () => {
    // queueIds select input is set to new values with filtered unsupported queues
    form.change('queueIds', unsupportedQueueModal.queueIds);

    // update queues options to match new skills selection
    const supportedQueuesOptionIdsRecord = recordFromIds(unsupportedQueueModal.supportedQueuesOptionIds, true);
    const supportedQueuesOptions = allQueues.filter(({ id }) => supportedQueuesOptionIdsRecord[id]);

    setQueuesOptions(supportedQueuesOptions);

    closeAndResetModal();
  };

  const handleModalClose = () => {
    // skillIds select input is reset back to previous values
    form.change('skillIds', unsupportedQueueModal.skillIds);

    closeAndResetModal();
  };

  const isSameEffectiveDate: TValidator = (shiftTemplateIds: [string]) => {
    if (!shiftTemplateIds.length) {
      return undefined;
    }

    const selectedEffectiveDates = shiftTemplateIds.map(
      (shiftTemplateId) => shiftTemplatesObj[shiftTemplateId].effectiveDate
    );

    if (new Set(selectedEffectiveDates).size !== selectedEffectiveDates.length) {
      return 'shiftTemplate.effectiveDateDuplicate';
    }

    return undefined;
  };

  const fullNameField = useField('fullName');
  const queuesField = useField('queueIds');
  const skillsField = useField('skillIds');

  return (
    <Stack direction="vertical" className="p-25 w-full" spacing={20}>
      <OnChange name="skillIds">
        {(skillIds: string[], previousSkillIds: string[]) => {
          const { queueIds: selectedQueueIds } = form.getState().values;

          if (!skillIds.length) {
            // If user doesn't have any Skill assigned yet, Supervisor can choose only from non-skill queues.
            const supportedQueues = allQueues.filter((queue) => queue.skillId === null);

            handleUnsupportedQueue(supportedQueues, previousSkillIds, selectedQueueIds ?? []);
          } else {
            // When user selects the Skill, queues are filtered to queues that Agent has skill for and non-skill.
            const supportedQueues = allQueues.filter((queue) =>
              skillIds.find((skill) => queue.skillId === skill || queue.skillId === null)
            );

            handleUnsupportedQueue(supportedQueues, previousSkillIds, selectedQueueIds ?? []);
          }
        }}
      </OnChange>

      <TextField
        {...fullNameField.input}
        className="w-full"
        isDisabled
        label={t('person.name')}
        testId={`${EDIT_AGENT_INPUT_FIELD}--fullName`}
      />

      <Stack direction="vertical" spacing={15}>
        <Heading as="h3" className="text-2xl text-blue-700">
          {t('queue.queues')}
        </Heading>

        <Select
          className="w-full"
          isClearable
          isDisabled={isView}
          isSearchable={shouldShowSearchField(queuesOptions.length)}
          label={t('queue.queues')}
          onSelectionChange={(newSelection) => queuesField.input.onChange(Array.from(newSelection))}
          options={queuesOptions}
          selectedKeys={queuesField.input.value}
          selectionMode="multiple"
          testId="queue-dropdown"
        />
      </Stack>

      <Stack direction="vertical" spacing={15}>
        <Heading as="h3" className="text-2xl text-blue-700">
          {t('skill.skills')}
        </Heading>

        <Select
          className="w-full"
          isClearable
          isDisabled={isView}
          isSearchable={shouldShowSearchField(allSkills.length)}
          label={t('skill.skills')}
          onSelectionChange={(newSelection) => skillsField.input.onChange(Array.from(newSelection))}
          options={allSkills}
          selectedKeys={skillsField.input.value}
          selectionMode="multiple"
          testId="skill-dropdown"
        />
      </Stack>

      <Stack direction="vertical" spacing={15}>
        <Heading as="h3" className="text-2xl text-blue-700">
          {t('shiftTemplate.shiftTemplatesWithEffectiveDate')}
        </Heading>

        <Field name="shiftTemplateIds" validate={isSameEffectiveDate}>
          {({ input, meta }) => (
            <Select
              className="w-full"
              errorMessage={t(meta.error)}
              isClearable
              isDisabled={isView}
              isSearchable={shouldShowSearchField(allShiftTemplates.length)}
              label={t('shiftTemplate.shiftTemplate', { count: 2 })}
              onBlur={input.onBlur}
              onFocus={input.onFocus}
              onSelectionChange={(newSelection) => input.onChange(Array.from(newSelection))}
              options={allShiftTemplates.map((shiftTemplate) => ({
                ...shiftTemplate,
                name: `${shiftTemplate.name} (${formatter.format(
                  parseDate(shiftTemplate.effectiveDate).toDate(timezone)
                )})`,
              }))}
              selectedKeys={input.value}
              selectionMode="multiple"
              testId="shiftTemplates-dropdown"
              validationState={meta.invalid ? 'invalid' : undefined}
            />
          )}
        </Field>
      </Stack>

      <ModalConfirmationNewStyle
        acceptCaption={t('layout.confirm.yes')}
        cancelCaption={t('layout.confirm.no')}
        isOpen={unsupportedQueueModal.open}
        onAccept={handleModalSubmit}
        onCancel={handleModalClose}
        title={t(`layout.warning`)}
      >
        {t('skill.modalBody')}
      </ModalConfirmationNewStyle>
    </Stack>
  );
};
